import { pointer, select, Selection } from 'd3-selection'
import { format as d3format } from 'd3-format'
import { brushX, brushY } from 'd3-brush'
import BaseChart from './BaseChart'
import buildScale from './buildScale'
import { css } from 'emotion'
import axisBuilder from './axis'
import selectionToSpan from './selectionToSpan'
import changeAxisOpacity from './changeAxisOpacity'
import Tooltip from './Tooltip'
import pointService from 'live/services/charts/point'
import { scaleLinear, ScaleLinear, ScaleTime } from 'd3-scale'
import Legend from './Legend'
import timeFormatter from './timeFormat'
import { AXIS_SIZE } from 'live/services/charts/axis.styled'
import SpanService from 'live/services/span'
import { ChartExtremes, ChartExtremesNotifier$ } from 'live/services/charts/chart-extremes'
import ZoomManager, {
    Zoom,
    ZoomIn,
    ZoomManagerZoomInNotifier$,
    ZoomManagerZoomOutNotifier$
} from 'live/services/zoom-manager'
import Utils from 'live/lib/utils'
import { Subscription } from 'rxjs'
import { SpanData, ZoomChartBehavior, State, SpanType } from '../../types'

export const axisCss = css`
    .tick {
        text {
            font-family: 'Lucida Grande', 'Lucida Sans Unicode', Arial, Helvetica, sans-serif;
            font-size: 11px;
            fill: #666;

            body.dark-theme & {
                fill: #ddd;
            }
        }
    }

    .domain,
    .tick line {
        stroke: var(--gray-300);
    }
`

const mainCss = css`
    height: 100%;
    width: 100%;
    display: flex;
    user-select: none;

    .contents {
        width: 100%;
        height: 100%;
        position: relative;
        flex: 1;
        svg {
            text {
                font-size: 11px;
                font-family: 'Lucida Grande', 'Lucida Sans Unicode', Arial, Helvetica, sans-serif;
            }
            text.mark {
                fill: #666;
                body.dark-theme & {
                    fill: #fff;
                }
            }
            line.mark {
                stroke-width: 1px;
                stroke: var(--gray-400);
                body.dark-theme & {
                    stroke: #ccd6eb;
                }
            }
        }
    }

    .track-header {
        display: flex;
        align-items: flex-end;
        justify-content: center;
    }

    &.horizontal {
        .track-header {
            align-items: center;
            justify-content: flex-end;
        }
    }
`

const widgetContainerCSS = css`
    &.imagelog-chart {
        .track-header {
            align-items: unset;
        }
    }
`

const axisWidthScale = scaleLinear([100, 200], [55, 82]).clamp(true)
export default abstract class TrackChart<T> extends BaseChart<T> implements ZoomChartBehavior {
    public _main: Selection<HTMLDivElement, unknown, null, undefined>
    protected _legend: Selection<HTMLDivElement, unknown, null, undefined>
    protected legend_: Legend
    protected _contents: Selection<HTMLDivElement, unknown, null, undefined>
    protected _svg: Selection<HTMLDivElement, unknown, null, undefined>

    private ZoomManagerZoomInSubscription: Subscription
    private ZoomManagerZoomOutSubscription: Subscription
    private registeredMouseEnterListener: boolean
    private registeredMouseLeaveListener: boolean

    protected innerWidth: number
    protected innerHeight: number

    protected tooltip: Tooltip<State>

    protected scale:
        | ScaleLinear<[number, number], [number, number]>
        | ScaleTime<[number, number], [number, number] | [Date, Date]>

    private mouseoverPoint: (p: { x: number | null; chart: typeof Object }) => null

    private subscription = Function.prototype
    private crossHairCenterOffset = 1

    protected chartExtremes: ChartExtremes
    private chartExtremesSubscription: Subscription
    protected data: T & SpanData
    public isOnZoom: boolean

    isValidChartExtremes(chartExtremes: ChartExtremes): boolean {
        return chartExtremes && Utils.isNumber(chartExtremes.min) && Utils.isNumber(chartExtremes.max)
    }

    init(): void {
        this.mouseoverPoint = (e: MouseEvent, chart: any): void => {
            try {
                if (e && e.target && chart !== this) {
                    const x = e.target.x
                    const value = this.scale(x)
                    const xValue = this.isVertical ? -10 : value
                    const yValue = !this.isVertical ? this.innerHeight : value
                    this._onMouseMove([xValue, yValue], this.scale)
                }
            } catch (e) {
                console.error(e)
            }
        }
        this.leave = () => this.onMouseLeave()

        const hasNewApi = Boolean(pointService.currentPoint$)

        if (hasNewApi) {
            this.subscription = pointService?.currentPoint$?.subscribe?.(p => {
                if (p.chart === this) return
                if (p.x === null) {
                    return this.onMouseLeave()
                }

                const value = this.scale(p.x)
                const xValue = this.isVertical ? -10 : value
                const yValue = !this.isVertical ? this.innerHeight : value
                this._onMouseMove([xValue, yValue], this.scale)
            })
        } else {
            pointService.addEventListener('mouseover', this.mouseoverPoint)
            pointService.addEventListener('mouseleave', this.leave)
        }

        this.el.classed('track-chart', true).classed(widgetContainerCSS, true)

        this._main = this.el
            .selectAll(`div.${mainCss}`)
            .data([null])
            .join('div')
            .classed(mainCss, true)
            .classed('vertical', this.isVertical)
            .classed('horizontal', !this.isVertical)
            .style('flex-direction', this.isVertical ? 'column' : 'row')

        this._legend = this._main
            .selectAll('div.track-header')
            .data([null])
            .join('div')
            .classed('track-header', true)
            .style('position', 'relative')

        this._contents = this._main.append('div').classed('contents', true)

        const svg = this._contents.append('svg') as Selection<HTMLDivElement, unknown, null, undefined>
        if (svg) {
            this._svg = svg
                .style('z-index', 1)
                .style('position', 'absolute')
                .attr('height', '100%')
                .attr('width', '100%')
        }

        if (ChartExtremesNotifier$ && !this.chartExtremesSubscription) {
            this.chartExtremesSubscription = ChartExtremesNotifier$.subscribe(this.setChartAxisExtremes.bind(this))
        }

        if (typeof this.initializeZoom === 'function') {
            this.initializeZoom()
        }
    }

    get legendSize(): number {
        return this.numberOfVisibleAxes * AXIS_SIZE
    }

    updateLegendSize(): void {
        return this._legend
            .style('height', this.isVertical ? `${this.legendSize}px` : 'auto')
            .style('width', this.isVertical ? 'auto' : `${this.legendSize}px`)
    }

    updateLayout(): void {
        this.el
            .selectAll(`div.${mainCss}`)
            .data([null])
            .join('div')
            .classed(mainCss, true)
            .classed('vertical', this.isVertical)
            .classed('horizontal', !this.isVertical)
            .style('flex-direction', this.isVertical ? 'column' : 'row')

        this._main
            .selectAll('div.track-header')
            .data([null])
            .join('div')
            .classed('track-header', true)
            .style('position', 'relative')

        this.updateLegendSize()
        const node = this.el.node()
        if (node) {
            const { width, height } = node.getBoundingClientRect()

            this.innerWidth = Math.max(
                width - this.margin.left - (this.isVertical ? this.axisSize : this.margin.right + this.legendSize),
                0
            )
            this.innerHeight = Math.max(
                0,
                height - this.margin.top - this.margin.bottom - (this.isVertical ? this.legendSize : this.axisSize)
            )
        }
    }

    buildScale(data: any): ScaleLinear<number, number> | ScaleTime<number, number> {
        return buildScale(data, this.isVertical ? this.innerHeight : this.innerWidth, this.spanPickerType)
    }

    get yAxisHeight(): number {
        return 28
    }

    get legend() {}

    get inner(): { top: number; left: number; right?: number; bottom: number } {
        return {
            top: this.margin.top,
            left: this.isVertical && !this.internalAxis ? this.margin.left + this.axisSize : this.margin.left,
            bottom: this.isVertical ? this.margin.top : this.margin.top + this.innerHeight
        }
    }

    get axisSize(): number {
        // horizontal axis
        if (!this.isVertical) {
            return 36
        }

        if (this.isVertical && (this.internalAxis || this.hideXAxis)) {
            return 0
        }

        return axisWidthScale(this.width)
    }

    abstract onMouseMove(mouse: [number, number], scale: ScaleLinear): void

    _onMouseMove(mouse: [number, number], scale: ScaleLinear, updateAll = false): void {
        const format = this.spanPickerType === 'time' ? timeFormatter : d3format('.2f')

        const x = mouse[this.isVertical ? 1 : 0]

        const xOffset = this.isVertical ? this.margin.top + this.crossHairCenterOffset : this.margin.left

        const value = scale.invert(x)

        if (updateAll) {
            this.highlightAll(value)
        }

        if (isNaN(value)) {
            return
        }

        ZoomManager.setCrosshairPosition(value)

        const xWithOffset = x + xOffset

        // TODO: rename to crosshair, its a xAxis property
        this._svg
            .selectAll('line.mark')
            .style('visibility', 'visible')
            .attr('shape-rendering', 'crispEdges')
            .attr('x1', this.isVertical ? this.margin.left + this.axisSize - 7 : xWithOffset)
            .attr('x2', this.isVertical ? this.margin.left + this.axisSize + this.innerWidth : xWithOffset)
            .attr('y1', this.isVertical ? xWithOffset : this.margin.top)
            .attr('y2', this.isVertical ? xWithOffset : this.margin.top + this.innerHeight)

        this._svg
            .selectAll('text.mark')
            .style('visibility', 'visible')
            .attr('text-anchor', this.isVertical ? (this.internalAxis ? 'start' : 'end') : 'middle')
            .attr('alignment-baseline', this.isVertical ? 'middle' : 'hanging')
            .attr('x', this.isVertical ? this.margin.left + (this.internalAxis ? 10 : this.axisSize - 10) : xWithOffset)
            .attr(
                'y',
                this.isVertical ? (this.internalAxis ? -9 : 0) + xWithOffset : this.margin.top + this.innerHeight + 9
            )
            // .text(format(value))
            .call(s => {
                const text = format(value)
                if (
                    (this.isVertical && this.internalAxis) ||
                    !this.isVertical ||
                    (this.isVertical && this.axisSize > 65)
                ) {
                    s.text(text)

                    return
                }

                const tokens = text.trim().split(' ')

                if (tokens.length === 2 && (tokens[1] === 'PM' || tokens[1] === 'AM')) {
                    s.selectAll('tspan')
                        .data(tokens)
                        .join('tspan')
                        // .attr('dx', -8)
                        .attr('dy', (d, i) => (i === 0 ? 0 : '1.1em'))
                        .attr('dx', (d, i) => (i === 0 ? 0 : '-1.51em'))
                        .text(d => d)
                } else {
                    s.text(text)
                }
            })

        this._svg.selectAll('g.xAxis .tick text').call(changeAxisOpacity, xWithOffset, scale, this.spanPickerType)

        if (typeof this.onMouseMove === 'function') {
            this.onMouseMove(mouse, scale)
        }
    }

    onMouseEnter(): void {}

    onMouseLeave(): void {
        this._svg.selectAll('g.xAxis').selectAll('.tick text').style('opacity', 1)
        this._svg.selectAll('.mark').style('visibility', 'hidden')
        if (this.tooltip) {
            this.tooltip.hide()
        }
        if (this.legend_) {
            this.legend_.clearHover()
        }
        if (this.renderTooltip) {
            this.renderTooltip(null, '')
        }
    }

    updateSpan(span): void {
        super.updateSpan(span)
        this._svg.selectAll('.mark').style('visibility', 'hidden')
    }

    get internalAxis(): boolean {
        return false
    }

    abstract renderLegend(data: T & SpanData): void

    render(data: T & SpanData): void {
        this.data = data

        this.updateLayout()

        if (!this.data) {
            return
        }
        this.renderLegend?.(data)

        this.scale = this.buildScale(this.data)
        const scale = this.scale

        if (this.isValidChartExtremes(this.chartExtremes)) {
            scale.domain([this.chartExtremes.min, this.chartExtremes.max])
        }

        if (this.isValidChartExtremes(ZoomManager.getPreviousZoom(this.getSpanType()))) {
            const { min, max } = ZoomManager.getPreviousZoom(this.getSpanType())
            this.scale.domain([min, max])
        }

        // draw axis
        if ((!this.hideXAxis && this.isVertical) || !this.isVertical) {
            this._svg
                .selectAll('g.xAxis')
                .data([this.scale.range().join('-')])
                .join('svg:g')
                .classed('xAxis', true)
                .classed(axisCss, true)
                .attr('transform', `translate(${this.inner.left} ${this.inner.bottom})`)
                .call(
                    axisBuilder,
                    scale,
                    this.isVertical,
                    this.spanPickerType,
                    this.axisSize,
                    this.internalAxis,
                    this.isValidChartExtremes?.(this.chartExtremes)
                        ? {
                              start: this.chartExtremes.min,
                              end: this.chartExtremes.max,
                              timestamp: this.data.span.timestamp
                          }
                        : this.data.customSpan || this.data.span,
                    SpanService.isRealtime(this.data.start?.info?.span.text)
                )
        }
        const brush = (this.isVertical ? brushY() : brushX()).on('end', brushed).extent([
            [0, 0],
            [this.innerWidth, this.innerHeight]
        ])

        function brushed(event): void {
            if (event.selection) {
                const span = selectionToSpan(event.selection, scale)
                self.updateSpan(span)
                select(this).call(brush.move, null)
            }
        }

        this._svg.selectAll('line.mark').data([null]).join('line').attr('class', 'mark')

        this._svg.selectAll('text.mark').data([null]).join('text').attr('class', 'mark')

        const self = this

        const brushSelection = this._svg.selectAll('g.brush')

        brushSelection
            .data([null])
            .join('g')
            .attr('class', 'brush')
            .attr('transform', `translate(${this.inner.left} ${this.inner.top})`)
            .call(brush)
            .selectAll('.overlay') // using .brush.overlay to monitor current itens
            .on('mouseenter', () => this.onMouseEnter())
            .on('mouseleave', () => this.onMouseLeave())
            .on('mousemove', event => {
                const mouse = pointer(event, brushSelection.node())
                this._onMouseMove(mouse, scale, true)
            })
    }

    get dataHeight(): number {
        return this.isVertical ? this.innerWidth : this.innerHeight
    }

    get dataWidth(): number {
        return this.isVertical ? this.innerHeight : this.innerWidth
    }

    grid() {
        return this._svg.selectAll('g.data').selectAll('g.grid').data([null]).join('g').lower().classed('grid', true)
    }

    plotHorizontalLines(yScale: ScaleLinear, points: any[]) {
        this.grid()
            .selectAll('line.horizontal')
            .data(points)
            .join('line')
            .classed('horizontal', true)
            .attr('shape-rendering', 'crispEdges')
            .attr('stroke', '#e6e6e6')
            .attr('stroke-width', '1px')
            .attr('y1', d => yScale(d))
            .attr('y2', d => yScale(d))
            .attr('x1', 0)
            .attr('x2', this.dataWidth)
    }

    plotVerticalLines(xScale: ScaleLinear, points: any[]) {
        this.grid()
            .selectAll('line.vertical')
            .data(points)
            .join('line')
            .classed('vertical', true)
            .attr('shape-rendering', 'crispEdges')
            .attr('stroke', '#e6e6e6')
            .attr('stroke-width', '1px')
            .attr('x1', d => xScale(d))
            .attr('x2', d => xScale(d))
            .attr('y1', 0)
            .attr('y2', this.dataHeight)
    }

    destroy(): void {
        if (this._main) {
            this._main.remove()
        }
        try {
            pointService.removeEventListener('mouseover', this.mouseoverPoint)
            pointService.removeEventListener('mouseleave', this.leave)

            this.subscription?.unsubscribe?.()
        } catch (e) {
            console.error(e)
        }
        if (this.tooltip) {
            this.tooltip.destroy()
        }
        if (typeof this.chartExtremesSubscription?.unsubscribe === 'function') {
            this.chartExtremesSubscription.unsubscribe()
        }

        if (typeof this.destroyZoom === 'function') {
            this.destroyZoom()
        }
    }

    setChartAxisExtremes(chartExtremes: ChartExtremes): void {
        this.chartExtremes = chartExtremes
        this.render(this.data)
    }

    initializeZoom(): void {
        if (!this.ZoomManagerZoomInSubscription) {
            this.ZoomManagerZoomInSubscription = ZoomManagerZoomInNotifier$.subscribe(this.setZoomIn.bind(this))
        }

        if (!this.ZoomManagerZoomOutSubscription) {
            this.ZoomManagerZoomOutSubscription = ZoomManagerZoomOutNotifier$.subscribe(this.setZoomOut.bind(this))
        }

        if (!this.registeredMouseEnterListener && typeof this.el?.on === 'function') {
            this.registeredMouseEnterListener = true

            this.el.on('mouseenter', this.handleMouseEnter.bind(this))
        }

        if (!this.registeredMouseLeaveListener && typeof this.el?.on === 'function') {
            this.registeredMouseLeaveListener = true
            this.el.on('mouseleave', this.handleMouseLeave.bind(this))
        }
    }

    destroyZoom(): void {
        if (typeof this.ZoomManagerZoomInSubscription?.unsubscribe === 'function') {
            this.ZoomManagerZoomInSubscription.unsubscribe()
        }

        if (typeof this.ZoomManagerZoomOutSubscription?.unsubscribe === 'function') {
            this.ZoomManagerZoomOutSubscription.unsubscribe()
        }

        if (this.registeredMouseEnterListener) {
            this._contents.on('mouseenter')
            this.registeredMouseEnterListener = false
        }

        if (this.registeredMouseLeaveListener) {
            this._contents.on('mouseleave')
            this.registeredMouseLeaveListener = false
        }
    }

    setZoomIn(zoom: ZoomIn): void {
        if (this.isWidgetDetachedFromDashboardSpan()) {
            return
        }

        if (zoom.newZoomBehavior && zoom.spanType === this.getSpanType()) {
            this.isOnZoom = true
            this.setAxisExtremes(zoom)
        }
    }

    setZoomOut(zoom: Zoom): void {
        if (this.isWidgetDetachedFromDashboardSpan()) {
            return
        }

        if (zoom.newZoomBehavior && zoom.spanType === this.getSpanType()) {
            if (zoom.resetZoom) {
                const originalSpan = this.getSpan()
                this.setAxisExtremes({
                    ...zoom,
                    min: originalSpan.begin,
                    max: originalSpan.end
                })
                this.data.customSpan = null
                this.chartExtremes = null

                return
            }

            this.isOnZoom = true
            this.setAxisExtremes(zoom)
        }
    }

    setAxisExtremes(zoom: ZoomIn | Zoom): void {
        const { min, max } = zoom

        this.chartExtremes = { min, max, spanType: this.getSpanType() }
        ZoomManager.setPreviousZoom({
            widgetType: '', // We don't use the widgetType in new zoom behavior
            spanType: this.getSpanType(),
            min: min,
            max: max,
            newZoomBehavior: true,
            chartInRealTime: false,
            timeZoomOcurred: new Date().getTime()
        })

        const newData = {
            ...this.data,
            customSpan: {
                begin: min,
                end: max
            }
        }

        this.render(newData)
    }

    handleMouseEnter(): void {
        const prevExtreme = this.getSpanFromScale()

        if (!Utils.isNumber(prevExtreme?.min) || !Utils.isNumber(prevExtreme?.max)) return

        if (this.isWidgetDetachedFromDashboardSpan()) return

        ZoomManager.setSpanTypeExtremes(this.getSpanType(), this.getSpan())

        ZoomManager.setPreviousZoom({
            widgetType: '', // We don't use the widgetType in new zoom behavior
            spanType: this.getSpanType(),
            min: prevExtreme.min,
            max: prevExtreme.max,
            newZoomBehavior: true,
            chartInRealTime: false,
            timeZoomOcurred: new Date().getTime()
        })

        ZoomManager.setHoveredWidgetSpanType(this.getSpanType())
        ZoomManager.setRealtimeSpan(this.getSpanType(), this.isRealtimeSpan())
    }

    handleMouseLeave(): void {
        ZoomManager.setHoveredWidgetSpanType(null)
        ZoomManager.setCrosshairPosition(null)
        this.isOnZoom = false
    }

    getSpanFromScale(): { min: number; max: number } {
        if (!this.scale && !this.scale.domain) return
        let [min, max] = this.scale.domain()

        if (min instanceof Date) {
            min = min.getTime()
        }

        if (max instanceof Date) {
            max = max.getTime()
        }

        return { min, max }
    }

    getSpan(): Partial<SpanType> {
        const span: SpanType = this.data.span

        const isValidBegin = Utils.isNumber(span.begin)

        const begin = isValidBegin ? span.begin : span.start
        const end = span.end

        return { begin, end }
    }

    getSpanType(): string {
        return this.options.cfg.spanPicker?.type || 'time'
    }

    isWidgetDetachedFromDashboardSpan(): boolean {
        return !!this.options?.dashboardWidgetContext?.state?.params?.spanDetached
    }

    isRealtimeSpan(): boolean {
        const span = this.data.start?.info?.span?.normalized || ''

        return SpanService.isRealtime(span)
    }
}
